import React, { useContext } from 'react';
import { UnitsContext } from '../../../../Contexts/UnitsContext';
import { UserContext } from '../../../../Contexts/UserContext';
import { Modalv2Context } from '../../../../Contexts/Modalv2Context';
import { useNavigate } from 'react-router-dom';
import {
	getJsonDiff,
	getUnsyncedAdUnits,
	handleFramerateSiteDocUpdation
} from '../../../../utilities/helpers';
import { siteSync, syncDfpAdUnits } from '../../../../api/sites';
import { toast } from 'react-toastify';
import ReactJson from 'react-json-view';
import {
	EXPB_DOC_CONSTANTS,
	CONFIG_SYNC_MODES,
	CONFIG_EDIT_MODES,
	DASHBOARD_PANELS
} from '../../../../core/constants';
import { pulseAnimationStyle } from './styles';

//mui imports
import { Box } from '@mui/system';
import { Button, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import useStyles from '../../styles';
import ReportIcon from '@mui/icons-material/Report';
import Tooltip from '@mui/material/Tooltip';
//===========

const { BLOCKERS } = EXPB_DOC_CONSTANTS;

const FinalOverview = React.forwardRef(() => {
	const classes = useStyles();
	const {
		mode,
		allValidSiteTags,
		dfpNetworkConfig,
		getFinalOverviewObj,
		previousCouchBaseConfig,
		setPreviousCouchBaseConfig,
		expbDoc,
		previousExpbDoc,
		initializeConfigData,
		curSelectedUnit
	} = useContext(UnitsContext);
	const { user } = useContext(UserContext);
	const { updateModalContent } = useContext(Modalv2Context);
	const navigate = useNavigate();
	const overviewObject = getFinalOverviewObj();

	const openInSuperAdmin = () => {
		const search = new URLSearchParams({
			panel: DASHBOARD_PANELS.SUPER_ADMIN,
			docId: `inst::${overviewObject.siteId}`
		});
		updateModalContent(null);
		navigate(`/dashboard?${search}`);
	};

	const updateSiteConfigDisabled = expbDoc && expbDoc.blockers.includes(BLOCKERS.SITE_UPDATE);
	const syncSiteDisabled = expbDoc && expbDoc.blockers.includes(BLOCKERS.SITE_SYNC);

	/**
	 * @name handleUnsyncedDfpAdUnits
	 * @description Function to handle the unsynced adUnits if there exists any by syncing them through INSTREAM_DFP_SYNC queue.
	 * @param {Object} overviewObject - The final overview object
	 * @param {Array} unsyncedAdUnitsArray - The array of unsynced adUnits
	 */
	const handleUnsyncedDfpAdUnits = async (overviewObject, unsyncedAdUnitsArray) => {
		const unsyncedAdUnitsPayload = {
			siteId: overviewObject?.siteId,
			siteDomain: overviewObject?.siteDomain,
			shouldSyncSite: !syncSiteDisabled, // false if sync is blocked in expbDoc, else true
			data: unsyncedAdUnitsArray,
			mode: CONFIG_SYNC_MODES.DFP_SYNC,
			previousCouchBaseConfig: {}
		};
		await syncDfpAdUnits(unsyncedAdUnitsPayload);
	};

	const handleSubmit = async () => {
		if (!user) {
			toast.error('No user found!', { position: toast.POSITION.BOTTOM_RIGHT });
			return;
		}
		if (!user.isAdmin) {
			toast.error('User not allowed to create or update!!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}

		const expbDocDelta = getJsonDiff(previousExpbDoc.current, expbDoc);
		// If expbDocDelta is not empty, then expbDoc must be updated before updating site
		if (Object.keys(expbDocDelta).length > 0 && mode === 'UPDATE') {
			return toast.error('ExbDoc Must be updated before updating site', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}

		try {
			const overviewObject = getFinalOverviewObj();
			console.log('Final Overview object is : ', overviewObject);

			const data = await handleFramerateSiteDocUpdation({
				mode,
				overviewObject,
				previousCouchBaseConfig,
				allValidSiteTags,
				dfpNetworkConfig,
				initializeConfigData,
				curSelectedUnit,
				setPreviousCouchBaseConfig,
				shouldValidateChanges: true // Validate changes for manual site updates
			});

			toast.success(data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
			if (mode === 'CREATE') {
				navigate(`/update-config/${overviewObject?.siteId}`);
			}
		} catch (err) {
			const errorMessage = err?.response?.data?.data?.message || err?.message;
			toast.error(errorMessage, { position: toast.POSITION.BOTTOM_RIGHT });
			console.error('UnitConfigGenerator.jsx logs : ', err);
		}
	};

	const handleSyncSite = async () => {
		try {
			const overviewObject = getFinalOverviewObj();
			const unsyncedAdUnitsArray = await getUnsyncedAdUnits(overviewObject);

			if (unsyncedAdUnitsArray.length > 0) {
				// Syncing the unsynced ad units that'll eventually sync the site also.
				await handleUnsyncedDfpAdUnits(overviewObject, unsyncedAdUnitsArray);
				toast.success('New AdUnits Creation Initiated, Site will Sync Automatically!', {
					position: toast.POSITION.BOTTOM_RIGHT
				});
			} else {
				const mode = CONFIG_SYNC_MODES.SYNC;
				await siteSync(
					overviewObject?.siteId,
					overviewObject,
					previousCouchBaseConfig,
					mode,
					overviewObject?.siteDomain
				);
				toast.success('Site Sync Initiated!', { position: toast.POSITION.BOTTOM_RIGHT });
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.data?.message || error.message;
			toast.error(errorMessage, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	const isSRSite = expbDoc?.consumerConfigs?.length > 0;

	return (
		<>
			<Box
				style={{
					position: 'absolute',
					bottom: '6rem',
					right: '50%',
					transform: 'translate(50%,50%)',
					width: isSRSite ? '28rem' : '25rem',
					display: 'flex',
					justifyContent: 'space-between'
				}}
			>
				<Button variant="contained" onClick={handleSubmit} disabled={updateSiteConfigDisabled}>
					{mode} Doc
				</Button>
				<Button color="primary" variant="outlined" style={{ backgroundColor: 'white' }} onClick={handleSyncSite} disabled={syncSiteDisabled}>
					<span style={{marginRight: '0.5rem'}}>Sync Site</span>
					{isSRSite && !syncSiteDisabled ? (
						<Tooltip title="Site is Selectively Rolled out!" arrow placement="top">
							<ReportIcon fontSize="medium" sx={pulseAnimationStyle} />
						</Tooltip>
					): ''}
				</Button>
			</Box>
			<Box className={classes.finalOverview}>
				{mode === CONFIG_EDIT_MODES.UPDATE && user?.isAdmin && user?.isSuperAdmin && overviewObject?.siteId && (
					<Tooltip title="Edit document in Super Admin" arrow>
						<IconButton
							aria-label="Edit document in Super Admin"
							color="primary"
							onClick={openInSuperAdmin}
							sx={{
								position: 'absolute',
								top: '0.5rem',
								right: '0.5rem',
								zIndex: 1,
								backgroundColor: '#fff',
								'&:hover': { backgroundColor: '#f5f5f5' }
							}}
						>
							<EditIcon />
						</IconButton>
					</Tooltip>
				)}
				<Box className={classes.finalOverviewContent}>
					<ReactJson
						enableClipboard={true}
						displayDataTypes={false}
						src={overviewObject}
						name={false}
					/>
				</Box>
			</Box>
		</>
	);
});

export default FinalOverview;
